import { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"

import { DayClassDTO, DaySummaryDTO } from "../../../../../models/DaySummaryDTO"
import { cursoCorto } from "../../../../../utils/cursoCorto"
import { horarioBonito } from "../../../../../utils/horarioBonito"

import "./tableroDelDia.css"

/*
	Quien ya paso lista hoy y quien no, jornada por jornada.

	Lo pidio Esteban (2026-09-20): la coordinacion necesitaba saber, a media
	manana, cuantas listas faltan de la jornada y de quien son. Antes habia que
	abrir horario por horario para averiguarlo.

	Cuenta el dia entero, no la pagina que se este viendo: "faltan 4 de 12" no
	quiere decir nada si solo se miraron los primeros cien horarios. Por eso el
	tablero pide su propia lista al servidor, sin paginar.

	Los que faltan van arriba y abiertos; los que ya estan, escondidos detras de
	una linea. Lo que se viene a ver aqui es lo que falta.
*/

type Props =
{
	dia          : DaySummaryDTO | null
	plantel      : any
	cargando     : boolean
	onPasarLista : (clase : DayClassDTO) => void
	onRecargar   : () => void
	/** Null vuelve a hoy. */
	onCambiarDia?: (fecha : string | null) => void
	enHoy?       : boolean
}

/* 80 -> "8:00", 155 -> "15:30". Es el codigo de media hora que usa toda la app. */
const hora = (codigo : number) : string =>
{
	if(!codigo) return ""

	const h = Math.floor(codigo / 10)
	const m = codigo % 10 === 5 ? "30" : "00"

	return h + ":" + m
}

const MESES =
	["enero", "febrero", "marzo", "abril", "mayo", "junio",
	 "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"]

const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"]

const fechaLarga = (texto : string) : string =>
{
	const partes = String(texto || "").split("-")

	if(partes.length !== 3) return texto || ""

	//Sin new Date(texto): "2026-09-20" se lee como UTC y en Mexico sale el dia anterior.
	const fecha = new Date(+partes[0], +partes[1] - 1, +partes[2])

	return DIAS[fecha.getDay()] + " " + fecha.getDate() + " de " + MESES[fecha.getMonth()]
}

/* "2026-09-20" + n dias -> "2026-09-19". Sin new Date(texto), que lee UTC y en
   Mexico devuelve el dia anterior. */
const correrDia = (texto : string, dias : number) : string =>
{
	const partes = String(texto || "").split("-")

	if(partes.length !== 3) return texto || ""

	const fecha = new Date(+partes[0], +partes[1] - 1, +partes[2])

	fecha.setDate(fecha.getDate() + dias)

	return fecha.getFullYear() + "-" +
		String(fecha.getMonth() + 1).padStart(2, "0") + "-" +
		String(fecha.getDate()).padStart(2, "0")
}

const TableroDelDia = ({dia, plantel, cargando, onPasarLista, onRecargar, onCambiarDia, enHoy} : Props) =>
{
	//Que jornadas tienen abierta su lista de "ya pasaron".
	const [hechasAbiertas, setHechasAbiertas] = useState<{[j : number] : boolean}>({})

	const jornadas = useMemo
	(
		() =>
		{
			const inicioTarde = plantel?.afternoon_shift_start ?? 130
			const inicioNoche = plantel?.evening_shift_start   ?? 170

			const nombres =
			[
				plantel?.morning_shift_name   || "Matutina",
				plantel?.afternoon_shift_name || "Vespertina",
				plantel?.evening_shift_name   || "Nocturna"
			]

			const cajones : DayClassDTO[][] = [[], [], []]

			;(dia?.classes || []).forEach((clase) =>
			{
				const j = clase.startHours >= inicioNoche ? 2 : (clase.startHours >= inicioTarde ? 1 : 0)

				cajones[j].push(clase)
			})

			return cajones.map((clases, indice) =>
			({
				indice,
				nombre : nombres[indice],
				total  : clases.length,
				hechas : clases.filter((c) => c.done),
				faltan : clases.filter((c) => !c.done)
			}))
		},
		[dia, plantel]
	)

	const total  = jornadas.reduce((suma, j) => suma + j.total, 0)
	const hechas = jornadas.reduce((suma, j) => suma + j.hechas.length, 0)

	const renglon = (clase : DayClassDTO, pendiente : boolean) => <div
		className={pendiente ? "waTabFila pendiente" : "waTabFila"}
		key={clase.classId || clase.scheduleId}
	>
		<span className="waTabHora">
			{hora(clase.startHours)}
		</span>
		<span className="waTabQue">
			<span className="waTabCurso">
				{cursoCorto(clase.course)}
			</span>
			<span className="waTabDetalle">
				{
					[
						clase.mentor || "sin profesor",
						clase.classroom,
						horarioBonito(clase.schedule)
					].filter(Boolean).join(" · ")
				}
			</span>
		</span>
		{
			pendiente
					?
				<button type="button" className="waTabBoton" onClick={() => onPasarLista(clase)}>
					Pasar lista
				</button>
					:
				<span className="waTabHecha">
					<FontAwesomeIcon icon={solid("check")} />
					<span>
						Hecha
					</span>
				</span>
		}
	</div>

	return <section className="waTablero">
		<header className="waTabCabecera">
			<span className="waTabTitulo">
				Pase de lista
				{/*
					Se puede retroceder de dia: la coordinacion revisa lo de ayer y lo del
					fin de semana, no solo lo de hoy. El calendario sirve para saltar lejos
					sin tener que dar clic dia por dia. (Esteban, 2026-09-21)
				*/}
				{
					(!onCambiarDia || !dia?.date) ? <span className="waTabFecha">
						{fechaLarga(dia?.date || "")}
					</span> : <span className="waTabDias">
						<button type="button" className="waTabDiaPaso" disabled={cargando}
						  aria-label="Día anterior"
						  onClick={() => onCambiarDia(correrDia(dia.date, -1))}
						>
							<FontAwesomeIcon icon={solid("chevron-left")} />
						</button>
						<label className="waTabFecha">
							{fechaLarga(dia.date)}
							<input type="date" value={dia.date} disabled={cargando}
							  onChange={(evento) => {if(evento.target.value) onCambiarDia(evento.target.value)}}
							/>
						</label>
						<button type="button" className="waTabDiaPaso" disabled={cargando}
						  aria-label="Día siguiente"
						  onClick={() => onCambiarDia(correrDia(dia.date, 1))}
						>
							<FontAwesomeIcon icon={solid("chevron-right")} />
						</button>
						{
							enHoy ? null : <button type="button" className="waTabHoy" disabled={cargando}
							  onClick={() => onCambiarDia(null)}
							>
								Hoy
							</button>
						}
					</span>
				}
			</span>
			<span className="waTabResumen">
				{
					total < 1
							?
						(cargando ? "Cargando…" : "Ese día no hay clases con alumnos.")
							:
						hechas + " de " + total + " listas hechas"
				}
			</span>
			<button type="button" className="waTabRecargar" onClick={onRecargar} disabled={cargando}
			  aria-label="Actualizar"
			>
				<FontAwesomeIcon icon={solid("rotate-right")} spin={cargando} />
			</button>
		</header>

		{
			total < 1 ? null : <div className="waTabJornadas">{
				jornadas.filter((j) => j.total > 0).map((j) => <div className="waTabJornada" key={j.indice}>
					<div className="waTabJornadaCab">
						<span className="waTabJornadaNombre">
							{j.nombre}
						</span>
						<span className="waTabJornadaCuenta">
							{j.hechas.length} de {j.total}
						</span>
						{
							j.faltan.length < 1
									?
								<span className="waTabTodo">
									<FontAwesomeIcon icon={solid("check")} />
									<span>
										Completa
									</span>
								</span>
									:
								<span className="waTabPendientes">
									{j.faltan.length} sin pasar
								</span>
						}
					</div>

					{j.faltan.map((clase) => renglon(clase, true))}

					{
						j.hechas.length < 1 ? null : <>
							<button type="button" className="waTabVerHechas"
							  onClick={() => setHechasAbiertas({...hechasAbiertas, [j.indice] : !hechasAbiertas[j.indice]})}
							>
								<FontAwesomeIcon icon={hechasAbiertas[j.indice] ? solid("chevron-up") : solid("chevron-down")} />
								<span>
									{hechasAbiertas[j.indice] ? "Ocultar" : "Ver"} las {j.hechas.length} que ya están
								</span>
							</button>
							{!hechasAbiertas[j.indice] ? null : j.hechas.map((clase) => renglon(clase, false))}
						</>
					}
				</div>)
			}</div>
		}
	</section>
}

export default TableroDelDia
